import "@tento-ui/components/button-group-row";
import "@tento-ui/components/timer/timer-controls";

import { Duration } from "@tento-chrono";
import { html, LonaWebComponent } from "@tento-ui/component";
import { $$ } from "@tento-ui/fastdom";
import { component } from "@tento-ui/component-decorators";
import { ButtonGroupRow } from "@tento-ui/components/button-group-row";
import { css, Style } from "@tento-ui/component-styles";
import { Carousel } from "@tento-ui/components/carousel/carousel";
import { LonaTimerPage } from "./timer-page";
import { PomodoroTemplate } from "../template";
import { MathUtils } from "@tento-core/math";
import { Toast } from "@tento-ui/components/toast/toast";
import { PomodoroTimer } from "../instance";
import { TimerControls } from "@tento-ui/components/timer/timer-controls";
import { Timer } from "@tento-ui/components/timer/timer";

@component({
  name: "lona-pomodoro-mini-app",
})
export class PomodoroMiniApp extends LonaWebComponent {
  private $btnGroup: ButtonGroupRow<string> = this.hydrate("btn-grp");
  private $carousel: Carousel = this.hydrate("timer-container");

  private items: PomodoroMiniApp.Item[] = [];

  bind(props: PomodoroMiniApp.Props): this {
    const templates = props.items.map((i) => i.template);
    const generateTimer = (templateId: string, duration: Duration.Time) => {
      if (props.generateInstance) {
        return props.generateInstance(templateId, duration);
      }
      return Timer.make(duration);
    };

    this.$btnGroup.bind(props.items[0].template.identifier, templates);
    this.$btnGroup.onSelected = (tab) => {
      const configIndex = this.items.findIndex(
        (i) => i.template.identifier === tab,
      );
      if (configIndex < 0) return;
      this.$carousel.setPage(configIndex);
    };

    this.items = props.items.map((propItem) => {
      const template = propItem.template;
      const templateId = template.identifier;
      const existingItem = this.items.find(
        (i) => i.template.identifier === templateId,
      );
      const duration = existingItem?.durationOverride ?? template.duration;
      const timer = propItem.zombie ?? generateTimer(templateId, duration);

      const item: PomodoroMiniApp.Item = {
        template,
        timer,
        $page: undefined!,
        durationOverride: existingItem?.durationOverride,
      };

      const $page = LonaTimerPage.makeWith({
        ...template,
        timer,
        onDurationChange: (duration) => {
          item.durationOverride = duration;
        },
        onStateChange: (timerState) => {
          if (timerState.state !== "running" && timerState.state !== "paused")
            return;
          props.onUpdate?.({ ...timerState, templateId });
        },
        onTimerComplete: () => {
          Toast.info("timer finished");
          const state = timer.getState();
          props.onUpdate?.({ ...state, templateId });
          timer.reset();
        },
      });

      const $pageControls = TimerControls.make();
      $pageControls.bindTimer(timer);
      $pageControls.slot = "controls";
      $page.appendChild($pageControls);

      item.$page = $page;
      return item;
    });

    this.$carousel.domBind(
      this.items.map((i) => i.$page),
      this.items.length,
      {
        onScroll: (_from, to) => {
          const toFlr = Math.floor(to);
          const flrPrgs = MathUtils.clampInc(1 - Math.abs(to - toFlr), 0, 1);
          const toCeil = Math.ceil(to);
          const ceilPrgs = MathUtils.clampInc(1 - Math.abs(to - toCeil), 0, 1);

          this.items[toFlr].$page.bindProgress(flrPrgs);
          this.items[toCeil].$page.bindProgress(ceilPrgs);

          const pageIndex = Math.round(to);
          if (pageIndex < 0 || pageIndex >= this.items.length) return;

          const config = this.items[pageIndex].template;
          this.$btnGroup.bindSelected(config.identifier, false);
        },
      },
    );

    if (props.shouldNavigateToRunning) {
      this.navigateToRunning();
    }

    return this;
  }

  private navigateToRunning() {
    let runningPageIndex = -1;
    let firstActiveIndex = -1;

    for (let i = 0; i < this.items.length; i++) {
      const state = this.items[i].timer.getSimpleState();
      if (state === "running") {
        runningPageIndex = i;
        break;
      } else if (state === "paused" && firstActiveIndex < 0) {
        firstActiveIndex = i;
      }
    }

    const navIndex =
      runningPageIndex >= 0 ? runningPageIndex : firstActiveIndex;
    if (navIndex < 0) return;

    // Defer navigation to allow ButtonGroupRow to finish rendering
    $$.mutate(() => {
      this.$carousel.setPage(navIndex, this.items.length, false);
      this.$btnGroup.bindSelected(
        this.items[navIndex].template.identifier,
        false,
      );
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        height: 100%;
      }

      #root {
        margin: 4px;
        align-items: center;
        height: 100%;
        position: relative;
      }

      #content {
        padding-block: 0px;
        padding-bottom: 8px;
        flex-grow: 1;
        justify-content: center;
        align-self: stretch;
      }

      #timer-container {
        --std-carousel-container-box-shadow: none;
        --std-carousel-border-radius: 12px;
        height: 100%;
        width: 100%;
      }

      #btn-grp {
        position: absolute;
        left: 8px;
        top: 8px;
        width: calc(100% - 16px);
        z-index: 99;

        --bg-btn-group-underlay: var(--bg-overlay-on-solid);
      }

      #header {
        align-items: center;
        gap: 8px;
        padding: 0 8px;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <std-row id=header>
        <std-button-group-row id=btn-grp></std-button-group-row>
      </std-row>

      <std-col id=content>
        <std-carousel id=timer-container></std-carousel>
      </std-col>
    </std-col>
  `;
}

export namespace PomodoroMiniApp {
  export type Item = {
    template: PomodoroTemplate;
    timer: Timer;
    $page: LonaTimerPage;
    durationOverride?: Option<Duration.Time>;
  };

  export type Props = {
    items: {
      template: PomodoroTemplate;
      zombie?: Option<Timer>;
    }[];
    generateInstance?: (templateId: string, duration: Duration.Time) => Timer;
    onUpdate?: (state: PomodoroTimer.State) => void;
    shouldNavigateToRunning?: boolean;
  };
}
